/**
 * State that must live exactly as long as the JavaScript runtime, not as long as
 * one evaluation of a module.
 *
 * Module scope is not a stable lifetime. Metro Fast Refresh re-evaluates a module
 * (and every non-component module that imports it) whenever a file in that graph
 * changes, and the fresh evaluation gets fresh top-level state while the OLD
 * evaluation's live resources stay alive: native database handles, OS ownership
 * leases, in-flight promises. A module-level cache of such a resource therefore
 * turns one refresh into a second open of a database that is still owned. That is
 * how "Database is still owned by an open connection; replacement refused" reached
 * the actor database after an ordinary reload.
 *
 * Keying the state on `globalThis` makes re-evaluation re-attach to the SAME state
 * instead of duplicating it. A full reload replaces the runtime and therefore
 * `globalThis`, which is exactly the lifetime the owned resources have too. Use this
 * for anything that owns a native or OS resource; keep `onetime` / `p-memoize` for
 * pure, recomputable values.
 */
const REGISTRY = Symbol.for("han.runtime-scoped-state");

function getRegistry(): Map<string, unknown> {
  const existing: unknown = Reflect.get(globalThis, REGISTRY);
  if (existing instanceof Map) {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the registry is only ever created below with this shape
    return existing as Map<string, unknown>;
  }
  const registry = new Map<string, unknown>();
  Reflect.set(globalThis, REGISTRY, registry);
  return registry;
}

/**
 * Return the runtime-wide state registered under `key`, creating it on first use.
 * `create` runs at most once per JavaScript runtime.
 */
export function getRuntimeScopedState<T>(key: string, create: () => T): T {
  const registry = getRegistry();
  if (registry.has(key)) {
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- one key is only ever registered by one caller with one type
    return registry.get(key) as T;
  }
  const state = create();
  registry.set(key, state);
  return state;
}
